import { IconAward } from "@tabler/icons-react"
import { type CSSProperties, type FC, type ReactNode, type Ref } from "react"

import { cn } from "../utilities/tailwind"

type Props = {
	accentColor: string
	children: ReactNode
	className?: string
	elementRef?: Ref<HTMLDivElement>
	issuerName: string
	logo?: { filePath: string; isIcon: boolean } | null
	title: ReactNode
}
const Certificate: FC<Props> = ({ accentColor, children, className, elementRef, issuerName, logo, title }) => {
	const style: CSSProperties & { "--certificate-accent": string } = { "--certificate-accent": accentColor }

	return (
		<div
			ref={elementRef}
			style={style}
			className={cn(
				"relative flex min-h-150 w-full overflow-hidden bg-[#fcfbf7] px-10 py-11 text-center text-[#20252d] shadow-[0_20px_70px_rgba(15,23,42,0.12)] sm:px-18 sm:py-14",
				className,
			)}
		>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute inset-0 border-10 border-(--certificate-accent)"
			/>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute inset-4 border border-(--certificate-accent) opacity-30"
			/>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute top-0 left-1/2 h-2 w-48 -translate-x-1/2 bg-(--certificate-accent)"
			/>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute top-7 left-7 size-12 border-t-2 border-l-2 border-(--certificate-accent)"
			/>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute top-7 right-7 size-12 border-t-2 border-r-2 border-(--certificate-accent)"
			/>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute bottom-7 left-7 size-12 border-b-2 border-l-2 border-(--certificate-accent)"
			/>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute right-7 bottom-7 size-12 border-r-2 border-b-2 border-(--certificate-accent)"
			/>

			<div className="relative flex w-full flex-col items-center">
				<header className="flex max-w-3xl flex-col items-center">
					<p className="text-[11px] font-semibold tracking-[0.28em] text-[#6d747e] uppercase">{issuerName}</p>
					<h1 className="mt-5 text-3xl leading-tight font-semibold tracking-[0.08em] text-balance uppercase sm:text-4xl">
						{title}
					</h1>
					<div aria-hidden="true" className="mt-6 flex items-center gap-3">
						<span className="h-px w-16 bg-(--certificate-accent) opacity-50" />
						<span className="size-2 rotate-45 border border-(--certificate-accent)" />
						<span className="h-px w-16 bg-(--certificate-accent) opacity-50" />
					</div>
				</header>

				<div className="my-auto flex w-full max-w-3xl flex-col items-center gap-3 py-10">{children}</div>

				<footer className="flex w-full items-end justify-between gap-8 border-t border-[#20252d]/10 pt-6">
					<div className="flex min-h-14 min-w-0 flex-1 items-center text-left">
						{logo ? (
							<img
								alt=""
								src={logo.filePath}
								className={cn(
									"object-contain object-left",
									logo.isIcon ? "size-14" : "max-h-11 max-w-64",
								)}
							/>
						) : (
							<p className="text-sm font-semibold tracking-[0.18em] uppercase">{issuerName}</p>
						)}
					</div>
					<div
						aria-hidden="true"
						className="flex size-16 shrink-0 items-center justify-center rounded-full border-2 border-(--certificate-accent) text-(--certificate-accent)"
					>
						<IconAward className="size-9" stroke={1.4} />
					</div>
				</footer>
			</div>
		</div>
	)
}

type LineProps = { children?: ReactNode; className?: string }
export const CertificateLine: FC<LineProps> = ({ children, className }) => (
	<p className={cn("text-base leading-relaxed text-pretty text-[#626a75] sm:text-lg", className)}>{children}</p>
)

type ValueProps = { children?: ReactNode; className?: string }
export const CertificateValue: FC<ValueProps> = ({ children, className }) => (
	<p
		className={cn(
			"my-1 max-w-full text-2xl leading-tight font-semibold text-balance wrap-break-word sm:text-3xl",
			className,
		)}
	>
		{children}
	</p>
)

export default Certificate
